import {useEffect} from 'react';

import {Routes, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes} from 'react-router-dom';
import type {RouteObject} from 'react-router-dom';
import * as Sentry from '@ergeon/erg-utils-js/exports/sentry';
import getExtendedOptions from '@ergeon/erg-utils-js/exports/sentry/getExtendedOptions';
import {
  getActiveSpan,
  getCurrentScope,
  getRootSpan,
  init as initSentrySdk,
  reactRouterV7BrowserTracingIntegration,
  replayIntegration,
  SEMANTIC_ATTRIBUTE_SENTRY_SOURCE,
  setTags,
  spanToJSON,
  thirdPartyErrorFilterIntegration,
  withSentryReactRouterV7Routing,
} from '@sentry/react';
import type {BrowserOptions, ReplayFrameEvent} from '@sentry/browser';
import type {ErrorEvent, Integration} from '@sentry/core';
import type {User} from '@ergeon/erg-auth';

import {SENTRY_DSN} from '../constants';
import {getPackageVersionTags} from './packageVersions';
import {SENTRY_APPLICATION_KEY} from './sentryApplicationKey';

export {captureException} from '@ergeon/erg-utils-js/exports/sentry';

/**
 * `<Routes>` that names pageload and navigation transactions by the matched route pattern.
 * Call it after `init()`: before the router integration is set up, Sentry hands back plain `<Routes>`.
 */
export const createTracedRoutes = () => withSentryReactRouterV7Routing(Routes);

// Attaches the authenticated user to every Sentry event, so issues show
// how many (and which) users are affected. Clears the user on sign-out.
export const setSentryUser = (user?: User) => {
  Sentry.setUser(
    user
      ? {id: user.gid, email: user.email, username: user.full_name, roles: user.roles}
      : null,
  );
};

const REDACTED = '[redacted]';

const isRecord = (value: unknown): value is {[key: string]: unknown} =>
  typeof value === 'object' && value !== null;

// Credentials travel inside request URLs: the quote-approval secret is a path
// segment (`/quote-approvals/<secret>/`), brief-mode receives that same secret
// as `?secret=`, and presigned det-state URLs carry their signature in the
// query string.
const SENSITIVE_QUERY_PARAM = new RegExp(
  '^(secret|token|signature|password|auth|api_?key|access_token' +
    '|x-amz-signature|x-amz-credential|x-amz-security-token)$',
  'i',
);
const SECRET_PATH_PARENTS = ['quote-approvals'];
const ABSOLUTE_URL = /^[a-z][a-z\d+.-]*:/i;

const redactPathSecrets = (pathname: string): string =>
  pathname
    .split('/')
    .map((segment, index, segments) =>
      (SECRET_PATH_PARENTS.includes(segments[index - 1] ?? '') ? REDACTED : segment))
    .join('/');

const redactQuerySecrets = (params: URLSearchParams) => {
  for (const key of Array.from(params.keys())) if (SENSITIVE_QUERY_PARAM.test(key)) params.set(key, REDACTED);
};

/** True when a URL carries a credential in a path segment or query value. */
export const carriesCredentials = (value: string): boolean => {
  try {
    const {pathname, searchParams} = new URL(value, window.location.origin);
    const secretSegment = pathname
      .split('/')
      .some((segment, index, segments) => Boolean(segment) && SECRET_PATH_PARENTS.includes(segments[index - 1] ?? ''));
    return secretSegment || Array.from(searchParams.keys()).some((key) => SENSITIVE_QUERY_PARAM.test(key));
  } catch {
    // Unparseable, so its shape is unknown — assume the worst.
    return true;
  }
};

/** The same URL with credential-bearing path segments and query values masked. */
const redactUrl = (value: string): string => {
  try {
    const url = new URL(value, window.location.origin);
    url.pathname = redactPathSecrets(url.pathname);
    redactQuerySecrets(url.searchParams);
    // Route breadcrumbs record paths, not absolute URLs; keep them that way
    // rather than expanding them against the current origin.
    return ABSOLUTE_URL.test(value) ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    // Not parseable as a URL, so its shape is unknown — it may still carry one.
    return REDACTED;
  }
};

/**
 * `extraErrorDataIntegration` (installed by the shared erg-utils options) copies
 * every enumerable own property of a thrown error into `contexts[<ErrorName>]`,
 * plus everything the error's `toJSON()` returns. On an `AxiosError` that means
 * `config` (the rep's `Authorization` header and the full request URL),
 * `request`, and `response` (the backend's payload) — so a failed quote or
 * calc-input load would ship the JWT, the quote-approval secret and the
 * presigned det-state signature to Sentry, and the replay attached to it.
 *
 * Axios' own opt-in `redact` config only covers `toJSON()`, which leaves the
 * enumerable `response.config.headers` behind; the context is rebuilt here from
 * the scalars worth triaging on instead. The exception itself (message, stack,
 * `status_class` tag) is untouched.
 */
const isAxiosContext = (context: unknown): context is {[key: string]: unknown} =>
  isRecord(context) && (context.isAxiosError === true || isRecord(context.config));

const asRecord = (value: unknown): {[key: string]: unknown} => (isRecord(value) ? value : {});

const scrubAxiosContext = (context: {[key: string]: unknown}) => {
  const config = asRecord(context.config);
  const {url} = config;
  const scrubbed: {[key: string]: unknown} = {
    code: context.code,
    method: config.method,
    status: context.status ?? asRecord(context.response).status,
    url: typeof url === 'string' ? redactUrl(url) : undefined,
  };
  return Object.fromEntries(Object.entries(scrubbed).filter(([, value]) => value !== undefined));
};

const scrubAxiosContexts = (event: ErrorEvent) => {
  const contexts = event.contexts ?? {};
  for (const [name, context] of Object.entries(contexts))
    if (isAxiosContext(context)) contexts[name] = scrubAxiosContext(context);
};

// The page URL and the XHR/fetch/navigation breadcrumbs carry the same secrets
// on every event raised from an approval-secret route, not only on the request
// that failed.
const BREADCRUMB_URL_KEYS = ['url', 'from', 'to'];

/** Masks every URL-bearing key of a breadcrumb-style `data` bag, in place. */
const redactUrlKeys = (data: unknown) => {
  if (!isRecord(data)) return;
  for (const key of BREADCRUMB_URL_KEYS) {
    const value = data[key];
    if (typeof value === 'string') data[key] = redactUrl(value);
  }
};

const scrubSensitiveData = (event: ErrorEvent) => {
  scrubAxiosContexts(event);
  const {request} = event;
  if (request?.url) request.url = redactUrl(request.url);
  (event.breadcrumbs ?? []).forEach(({data}) => redactUrlKeys(data));
};

const URL_BEARING_SPAN_OP = /^(resource|navigation)\./;

/** Masks credential-bearing URLs in a replay recording frame, in place. */
export const redactReplayFrame = (event: ReplayFrameEvent): ReplayFrameEvent => {
  const {data} = event;
  // `description` is the URL on these ops; a label on the rest (memory, paint).
  if (data.tag === 'performanceSpan' && URL_BEARING_SPAN_OP.test(data.payload.op))
    data.payload.description = redactUrl(data.payload.description);
  if (data.tag === 'breadcrumb') redactUrlKeys(data.payload.data);
  return event;
};

/**
 * Latches once a credential-bearing URL has been on screen. Buffer-mode replays
 * retain activity from before the error, so a page visited earlier in the
 * buffer window is still in the recording after navigating away.
 */
const createCredentialUrlLatch = () => {
  let seen = false;
  return () => {
    seen = seen || carriesCredentials(window.location.href);
    return seen;
  };
};

/**
 * Releases the SDK's one-Replay-per-page slot, held by an instance being
 * discarded.
 *
 * That guard is module-scope state, not per-client: the Replay constructor sets
 * it, and every later `replayIntegration()` call throws "Multiple Sentry Session
 * Replay instances are not supported". `getExtendedOptions` constructs its own
 * Replay eagerly while building its options object, so the guard is already
 * armed by the time the array reaches `replaceReplayIntegration` — filtering the
 * instance out of the array does not disarm it. Building the replacement then
 * threw out of `Sentry.init()`, which runs at App.tsx module scope, so the entry
 * module never reached `root.render` and every non-development build served a
 * blank page — a regression introduced when this app moved to the shared
 * `getExtendedOptions` and started replacing its Replay integration.
 *
 * The instance exposes the flag as an accessor onto that module-scope state, so
 * clearing it through the discarded instance frees the slot for a replacement
 * built through the public factory. Jest could not have caught this: the SDK
 * only throws when `isBrowser()` is true, and it is false under jsdom (`process`
 * is defined, so the SDK reads the environment as Node).
 */
const releaseReplaySingleton = (replay: Integration): boolean => {
  const slot = replay as unknown as {_isInitialized?: unknown};
  if (typeof slot._isInitialized !== 'boolean') return false;
  slot._isInitialized = false;
  return true;
};

/**
 * Replay uploads its own envelope, which never passes through `beforeSend`, so
 * none of the scrubbing above reaches it. erg-utils opts every ergeon.in
 * request into `networkDetailAllowUrls` (request/response bodies) and replay
 * frames record raw URLs — the same secrets the error path redacts, shipped at
 * `replaysOnErrorSampleRate: 1.0`. Drop the opt-in and redact what remains.
 *
 * `beforeAddRecordingEvent` cannot cover everything: it only runs for custom
 * frames (`maybeApplyCallback` gates on `isCustomEvent`), while rrweb's Meta
 * frame records `window.location.href` verbatim and the envelope's `urls`
 * keeps `pathname + hash + search`. Neither is reachable from any hook, so a
 * replay of an approval-secret page cannot be sanitized — skip it instead.
 */
export const replaceReplayIntegration = (
  integrations: Integration[],
  // Injectable so the one-per-page guard can be exercised in tests, where the
  // real integration never enforces it (see `releaseReplaySingleton`).
  createReplay: (options?: Parameters<typeof replayIntegration>[0]) => Integration = replayIntegration,
): Integration[] => {
  const shared = integrations.find(({name}) => name === 'Replay');
  if (shared && !releaseReplaySingleton(shared)) {
    // The redacting replacement cannot be built. Dropping replay altogether is
    // the safe failure: keeping the shared instance would ship exactly the
    // secrets this function exists to withhold. Errors still report, without a
    // recording attached.
    console.warn('Sentry: replay slot could not be released; session replay disabled');
    return integrations.filter(({name}) => name !== 'Replay');
  }

  const sawCredentialUrl = createCredentialUrlLatch();
  return [
    ...integrations.filter(({name}) => name !== 'Replay'),
    createReplay({
      blockAllMedia: true,
      maskAllText: true,
      beforeAddRecordingEvent: (event) => {
        sawCredentialUrl();
        return redactReplayFrame(event);
      },
      beforeErrorSampling: () => !sawCredentialUrl(),
    }),
  ];
};

const RESIZE_OBSERVER_LOOP_NOTICE = 'ResizeObserver loop completed with undelivered notifications.';

const isResizeObserverLoopNotice = ({exception}: ErrorEvent): boolean =>
  (exception?.values ?? []).some(({value}) => value === RESIZE_OBSERVER_LOOP_NOTICE);

/**
 * App-side scrubbing only. Grouping is deliberately NOT done here.
 *
 * This function used to re-implement the shared `applyFingerprints` rules, and
 * the two copies had already drifted: the local catalog pattern was broader than
 * the shared one, and the local shape-not-set rule invented an `'unknown'` frame
 * where the shared one falls back to Sentry's default grouping. Worse, it ran
 * AFTER the shared rules and assigned unconditionally, so it overwrote the
 * `['{{ default }}', package, operation]` ownership fingerprint that the
 * reporting API in `reporting.ts` sets — silently regrouping events that had
 * deliberately been grouped by their owning package.
 *
 * `erg-utils-js`'s `applyFingerprints` (invoked by the shared `beforeSend`, which
 * runs first) is now the single code-side source of grouping.
 */
export const sentryBeforeSend = (event: ErrorEvent): ErrorEvent | null => {
  if (isResizeObserverLoopNotice(event)) return null;
  scrubSensitiveData(event);
  return event;
};

const joinRoutePath = (base: string, path?: string) => {
  if (!path) return base;
  return path.startsWith('/') ? path : `${base.replace(/\/$/, '')}/${path}`;
};

const currentRoutePattern = (routes: RouteObject[]): string | undefined => {
  const matches = matchRoutes(routes, window.location);
  return matches?.reduce((pattern, {route}) => joinRoutePath(pattern, route.path), '') || undefined;
};

/**
 * Names the pageload transaction, and the scope errors report under, by the route pattern its URL
 * matches. The routes mount behind auth and lazy chunks, often after the pageload span has ended,
 * so the router integration cannot rename it.
 */
export const namePageloadByRoute = (routes: RouteObject[]) => {
  const active = getActiveSpan();
  const pattern = currentRoutePattern(routes);
  if (!active || !pattern) return;
  const pageload = getRootSpan(active);
  if (spanToJSON(pageload).op !== 'pageload') return;
  pageload.updateName(pattern);
  pageload.setAttribute(SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, 'route');
  getCurrentScope().setTransactionName(pattern);
};

/**
 * Keep erg-utils' sanitization, Replay, console capture and shared
 * fingerprinting, but remove its legacy message/URL drops. This app relies on
 * the application-key integration to tag third-party-only stacks instead;
 * inbound filters would otherwise discard Tawk, extension, gtag and other
 * events before that tag could be useful.
 *
 * The shared beforeSend also filters some console-captured messages. Those are
 * restored here while preserving its explicit HeadlessChrome/E2E suppression.
 */
export const getSentryOptions = (): BrowserOptions => {
  const extendedOptions = getExtendedOptions({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      thirdPartyErrorFilterIntegration({
        behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
        filterKeys: [SENTRY_APPLICATION_KEY],
      }),
    ],
    release: import.meta.env.SENTRY_RELEASE_NAME,
    tracesSampleRate: 0.05,
  });
  // getExtendedOptions always installs this wrapper.
  const sharedBeforeSend = extendedOptions.beforeSend!;
  const customIntegrations = replaceReplayIntegration(
    Array.isArray(extendedOptions.integrations) ? extendedOptions.integrations : [],
  );

  return {
    ...extendedOptions,
    // Removing the default inbound filter also disables the SDK's curated
    // message-drop list. Keeping it would still silently discard third-party
    // events such as cross-origin "Script error" before the tagger runs.
    integrations: (defaultIntegrations) => [
      ...defaultIntegrations.filter(({name}) => name !== 'InboundFilters' && name !== 'EventFilters'),
      ...customIntegrations,
    ],
    // Both arrays are consumed by Sentry's inbound filter event processor,
    // which drops matching events before beforeSend and cannot be undone there.
    denyUrls: [],
    ignoreErrors: [],
    beforeSend: async (event, hint) => {
      const sharedResult = await sharedBeforeSend(event, hint);
      if (sharedResult === null && window.navigator.userAgent.includes('HeadlessChrome')) return null;
      return sentryBeforeSend(sharedResult ?? event);
    },
  };
};

export const init = (routes: RouteObject[]) => {
  try {
    initSentrySdk(getSentryOptions());
    namePageloadByRoute(routes);
    setTags(getPackageVersionTags());
  } catch (error) {
    // Observability setup must never be able to take the app down with it.
    // `init()` is called at App.tsx module scope, so anything thrown here aborts
    // the entry module before `root.render` — the app renders an empty #root and
    // the error that caused it cannot be reported, because reporting is what
    // just failed. Degrade to no error reporting instead.
    console.warn('Sentry init failed; continuing without error reporting', error);
  }
};
